import { FormGroup } from '@angular/forms'

export type ComponentState = 'details' | 'edit' | 'create'

export type Gender = 'male' | 'female' | 'no_matter'

export type DisplayType =
  | 'text'
  | 'number'
  | 'translated'
  | 'icon'
  | 'valueAndIcon'
  | 'valueAndAvatar'
  | 'remainedData'
  | 'timeRange'
  | 'dateRange'
  | 'daysRange'
  | 'valueFromCallback'
  | 'date'
  | 'dateTimeRangeSplitWithPipe'
  | 'uploader'
  | 'valueFromCallbackBasedHTML'
  | 'valueAndImage'
  | 'avatarGroup'
  | 'checkbox'
  | 'doubleWithSpace'
  | 'doubleWithIdNumber'
  | 'knob'

export type InputType =
  | 'text'
  | 'number'
  | 'mobile'
  | 'dropdown'
  | 'multiselect'
  | 'textarea'
  | 'checkbox'
  | 'radio'
  | 'timeRange'
  | 'dateRange'
  | 'daysRange'
  | 'date'
  | 'mobile'
  | 'email'
  | 'map-address'
  | 'dateTime'

export interface DayWeekOption {
  value: number
  name?: string
  label?: string
}

export interface timeRange {
  restrictionId: number
  daysWeek: DayWeekOption[]
  hourFirstRange: Date
  hourSecondRange: Date
  formControl?: FormGroup
}

export class TableMenuOptionsConfig {
  label?: string
  icon: string
  iconClass: string
  actionName: string
  styleClass?: string
  disabledByKeyOperatorAndValue?: boolean
  keyCondition?: string
  operatorCondition?: '==' | '!=' | ('==' | '!=')[]
  valueCondition?: unknown
  hasPermission?: string
  tooltip?: string
  tooltipPosition?: string
}

export class TableColumnConfig {
  name: string
  label: string
  sortable?: boolean
  style?: string
  class?: string
  type?: DisplayType
  translatePrevLabel?: string
  whenTranslateUseUpperCase?: boolean
  editableConfig?: EditableColumnConfig
  remainedDataConfig?: RemainedDataConfig
  notEditableSecondRangeKey?: string
  valueFromCallback?: (args: never) => unknown
  trueIconClass?: string
  falseIconClass?: string
  customIcon?: boolean
  dateOrTimeAdditionalFormat?: string
  knobConfig?: {
    percentValueCallback: (args: never) => unknown
    totalValueCallback: (args: never) => unknown
  }
  tooltipCallback?: (args: never) => string
  tooltipPosition?: string
}

export class TableConfig {
  id: string
  data: unknown[]
  checkbox?: boolean
  style?: string | Record<string, unknown>
  scrollHeight?: string
  class?: string = 'p-datatable-striped'
  menuOptions?: TableMenuOptionsConfig[]
  columnsConfig: TableColumnConfig[]
  buttonsColumn: TableMenuOptionsConfig[]
  quickActionButton?: {
    icon: string
    iconClass?: string
    actionName: string
    tooltip?: string
    tooltipPosition?: string
  }
  /** When set, clicking anywhere on a data row emits onAction with this action name (rows become clickable) */
  rowClickActionName?: string
  showRowMenuConditionCallback?: (args: never) => unknown
  rowGroupOptions?: {
    dataKey: string
    sortMode?: 'single' | 'multiple'
    groupRowsBy?: string
    sortField?: string
  } | null = null
  headerClass?: string
  virtualScrollLazyLoadOptions?: {
    rows: number
    virtualScrollItemSize: number
    rowHeight: string
  }
  customFooter?: boolean
}

export class ListConfig {
  data: unknown[]
  optionValue?: string
  optionLabel?: string
  filter?: boolean
  template?: DisplayType
  translatePrevLabel?: string
  whenTranslateUseUpperCase?: boolean
  templateKey?: string
  multiSelectionEmptyLabel?: string
  multiSelectionSeparationValue?: string
  showClear?: boolean
  selectedItemTemplate?: boolean
  multiSelectionDisplay?: 'chip' | 'comma'
  emptyMessage?: string
  scrollHeight?: string
  initUsingSelectItemInterface?: boolean
  secondTemplateKey?: string
  lazy?: boolean
  selectionLimit?: number
  filterBy?: string
  optionDisabled?: string

  constructor(config: {
    data: unknown[]
    optionValue?: string
    optionLabel?: string
    filter?: boolean
    template?: DisplayType
    translatePrevLabel?: string
    whenTranslateUseUpperCase?: boolean
    templateKey?: string
    multiSelectionEmptyLabel?: string
    multiSelectionSeparationValue?: string
    showClear?: boolean
    selectedItemTemplate?: boolean
    multiSelectionDisplay?: 'chip' | 'comma'
    emptyMessage?: string
    scrollHeight?: string
    initUsingSelectItemInterface?: boolean
    secondTemplateKey?: string
    lazy?: boolean
    selectionLimit?: number
    filterBy?: string
    optionDisabled?: string
  }) {
    this.data = config.data
    this.optionValue = config.optionValue
    this.optionLabel = config.optionLabel
    this.filter = config.filter
    this.template = config.template
    this.translatePrevLabel = config.translatePrevLabel
    this.whenTranslateUseUpperCase = config.whenTranslateUseUpperCase
    this.templateKey = config.templateKey
    this.multiSelectionEmptyLabel = config.multiSelectionEmptyLabel
    this.multiSelectionSeparationValue = config.multiSelectionSeparationValue
    this.showClear = config.showClear
    this.selectedItemTemplate = config.selectedItemTemplate !== undefined ? config.selectedItemTemplate : true
    this.multiSelectionDisplay = config.multiSelectionDisplay || 'comma'
    this.emptyMessage = config.emptyMessage
    this.scrollHeight = config.scrollHeight || '250px'
    this.initUsingSelectItemInterface = config.initUsingSelectItemInterface
    this.secondTemplateKey = config.secondTemplateKey
    this.lazy = config.lazy
    this.selectionLimit = config.selectionLimit
    this.filterBy = config.filterBy
    this.optionDisabled = config.optionDisabled
  }
}

export class DateTimeConfig {
  constructor(
    format: string | null,
    beforeDataForCheckSameDays?: unknown,
    currentDataForCheckSameDays?: unknown,
    hoursKey?: string,
    additionalConditionKey?: string,
    dayKey?: string,
    firstRangeKey?: string,
    secondRangeKey?: string,
    firstRangePlaceholder?: string,
    secondRangePlaceholder?: string,
    iconClass?: string,
    mask: string = '99/9999'
  ) {
    this.format = format
    this.beforeDataForCheckSameDays = beforeDataForCheckSameDays
    this.currentDataForCheckSameDays = currentDataForCheckSameDays
    this.hoursKey = hoursKey
    this.additionalConditionKey = additionalConditionKey
    this.dayKey = dayKey
    this.firstRangeKey = firstRangeKey
    this.secondRangeKey = secondRangeKey
    this.firstRangePlaceholder = firstRangePlaceholder
    this.secondRangePlaceholder = secondRangePlaceholder
    this.iconClass = iconClass
    this.mask = mask
  }
  format: string | null
  beforeDataForCheckSameDays?: unknown
  currentDataForCheckSameDays?: unknown
  hoursKey?: string
  additionalConditionKey?: string
  dayKey?: string
  firstRangeKey?: string
  secondRangeKey?: string
  firstRangePlaceholder?: string
  secondRangePlaceholder?: string
  iconClass?: string
  mask?: string
}

export class RadioConfig {
  constructor(
    translateValue: boolean = true,
    trueValue: string | number | boolean = true,
    falseValue: string | number | boolean = false,
    trueLabel: string = 'COMMON.TAGS.ALLOW',
    falseLabel: string = 'COMMON.TAGS.DENY',
    additionalClasses: string = 'row-inline grid-sm'
  ) {
    this.translateValue = translateValue
    this.trueValue = trueValue
    this.falseValue = falseValue
    this.trueLabel = trueLabel
    this.falseLabel = falseLabel
    this.additionalClasses = additionalClasses
  }
  translateValue: boolean
  trueValue: string | number | boolean
  falseValue: string | number | boolean
  trueLabel: string
  falseLabel: string
  additionalClasses: string
}

export class EditableColumnConfig {
  type: InputType
  outputEmptyValue?: string
  disabled?: boolean
  listConfig?: ListConfig
  style?: string
  class?: string = 'p-input-filled'
  rangeColumnName?: string
  dateTimeConfig?: DateTimeConfig
  ngModelInvalidInput?: boolean
  onInputCallback?: (columnsConfig: never, row: never, value: never) => unknown
  listConfigRowsData?: unknown[]
  numberInputDecimal?: number
}

export class Language {
  id: number | string
  name: string
}

export class RemainedDataConfig {
  key?: string = 'name'
  remainedNumber?: number = 3
  callbackFunction: (args: never) => unknown
  remainedDataNoDataLabel?: string
  tooltipByKey?: string
}

export interface RadioListConfig {
  allowedModules: string[]
  label: string
  formControlName: string
  trueValue: string | number | boolean
  falseValue: string | number | boolean
}
